import React from 'react';
import { Brain, Heart, Activity, Moon } from 'lucide-react';

const MentalHealthScore = ({ scores = {} }) => {
  const defaultScores = {
    anxiety: 65, // Lower is better
    focus: 78,
    emotional: 72,
    sleep: 68,
  };

  const finalScores = { ...defaultScores, ...scores };

  const metrics = [
    {
      id: 'anxiety',
      label: 'Anxiety Level',
      icon: Brain,
      value: finalScores.anxiety,
      inverted: true, // Lower is better
      color: '#ef4444',
      insight: 'Moon + Mercury analysis',
    },
    {
      id: 'focus',
      label: 'Focus Ability',
      icon: Activity,
      value: finalScores.focus,
      inverted: false,
      color: '#6366f1',
      insight: 'Mercury strength',
    },
    {
      id: 'emotional',
      label: 'Emotional Balance',
      icon: Heart,
      value: finalScores.emotional,
      inverted: false,
      color: '#ec4899',
      insight: 'Moon position',
    },
    {
      id: 'sleep',
      label: 'Sleep Quality',
      icon: Moon,
      value: finalScores.sleep,
      inverted: false,
      color: '#8b5cf6',
      insight: 'Saturn + Moon',
    },
  ];

  const getStatusColor = (value, inverted) => {
    const effectiveValue = inverted ? 100 - value : value;
    if (effectiveValue >= 75) return '#10b981'; // Green
    if (effectiveValue >= 50) return '#f59e0b'; // Amber
    return '#ef4444'; // Red
  };

  const getStatusLabel = (value, inverted) => {
    const effectiveValue = inverted ? 100 - value : value;
    if (effectiveValue >= 75) return 'Good';
    if (effectiveValue >= 50) return 'Moderate';
    return 'Needs Attention';
  };

  return (
    <div className="space-y-4">
      {/* Overall Mental Health Score */}
      <div className="text-center mb-6">
        <div className="inline-flex items-center justify-center w-32 h-32 rounded-full bg-gradient-to-br from-cosmic-100 to-cosmic-200 mb-3">
          <div className="text-center">
            <div className="font-serif text-3xl font-medium text-cosmic-700">
              {Math.round((finalScores.focus + finalScores.emotional + finalScores.sleep + (100 - finalScores.anxiety)) / 4)}
            </div>
            <div className="text-xs text-cosmic-600">Overall</div>
          </div>
        </div>
        <p className="text-sm text-stone-600">
          Based on Moon + Mercury + Saturn analysis
        </p>
      </div>

      {/* Individual Metrics */}
      {metrics.map((metric) => {
        const Icon = metric.icon;
        const statusColor = getStatusColor(metric.value, metric.inverted);
        const statusLabel = getStatusLabel(metric.value, metric.inverted);
        const displayValue = metric.inverted ? 100 - metric.value : metric.value;

        return (
          <div
            key={metric.id}
            className="bg-white rounded-card p-4 shadow-card"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: `${metric.color}20` }}
                >
                  <Icon className="w-5 h-5" style={{ color: metric.color }} />
                </div>
                <div>
                  <h4 className="font-serif text-sm font-medium text-stone-900">
                    {metric.label}
                  </h4>
                  <p className="text-xs text-stone-500">{metric.insight}</p>
                </div>
              </div>
              <div className="text-right">
                <div className="font-serif text-xl font-medium text-stone-900">
                  {displayValue}
                </div>
                <div
                  className="text-xs font-medium"
                  style={{ color: statusColor }}
                >
                  {statusLabel}
                </div>
              </div>
            </div>

            {/* Progress bar */}
            <div className="h-2 bg-stone-100 rounded-full overflow-hidden">
              <div
                className="h-full transition-all duration-500"
                style={{
                  width: `${displayValue}%`,
                  backgroundColor: statusColor,
                }}
              />
            </div>
          </div>
        );
      })}

      {/* Recommendations */}
      <div className="bg-gradient-to-br from-sacred-50 to-white rounded-card p-4 mt-6">
        <h4 className="font-serif font-medium text-stone-900 mb-2 flex items-center gap-2">
          <span>💡</span>
          Personalized Recommendations
        </h4>
        <ul className="space-y-2 text-sm text-stone-700">
          {finalScores.anxiety > 50 && (
            <li className="flex items-start gap-2">
              <span className="text-cosmic-500">•</span>
              <span>Practice 10 minutes of pranayama during Moon's favorable hours (6-8 AM)</span>
            </li>
          )}
          {finalScores.focus < 75 && (
            <li className="flex items-start gap-2">
              <span className="text-cosmic-500">•</span>
              <span>Mercury is weak. Chant mantras on Wednesday mornings</span>
            </li>
          )}
          {finalScores.sleep < 70 && (
            <li className="flex items-start gap-2">
              <span className="text-cosmic-500">•</span>
              <span>Sleep before 10 PM during this Saturn transit for better rest</span>
            </li>
          )}
          <li className="flex items-start gap-2">
            <span className="text-cosmic-500">•</span>
            <span>Avoid caffeine after 4 PM when Moon enters your 6th house</span>
          </li>
        </ul>
      </div>
    </div>
  );
};

export default MentalHealthScore;
